import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$getRoot} from 'lexical';
import {
  $createGuidanceDecoratorNode,
  $isGuidanceDecoratorNode,
} from 'plugins/enhancedHeadings/GuidanceDecoratorNode';
import {useCallback, useEffect, useRef} from 'react';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';

/**
 * GuidancePlugin manages guidance decorator nodes for headings
 * that have guidance text configured.
 */
export const GuidancePlugin = () => {
  const [editor] = useLexicalComposerContext();

  // Flag to prevent infinite loops when managing guidance nodes
  const isManagingGuidance = useRef(false);

  const manageGuidanceNodes = useCallback(() => {
    if (isManagingGuidance.current) {
      return; // Prevent recursive calls
    }

    isManagingGuidance.current = true;
    editor.update(() => {
      const root = $getRoot();
      const children = root.getChildren();

      children.forEach((node, index) => {
        if (node instanceof EnhancedHeadingNode) {
          const headingId = node.getHeadingId();
          const guidance = node.getGuidance();
          const nextNode = children[index + 1];

          // Check if there's already a guidance node after this heading
          const hasGuidanceNode =
            nextNode &&
            $isGuidanceDecoratorNode(nextNode) &&
            (nextNode as any).getHeadingId() === headingId;

          if (guidance && guidance.trim()) {
            // We have guidance text - ensure there's a guidance node
            if (!hasGuidanceNode) {
              const guidanceNode = $createGuidanceDecoratorNode(headingId, guidance);
              node.insertAfter(guidanceNode);
            } else {
              // Update existing guidance node
              (nextNode as any).setGuidance(guidance);
            }
          } else {
            // No guidance text - remove guidance node if it exists
            if (hasGuidanceNode) {
              nextNode.remove();
            }
          }
        }
      });
    });

    // Reset the flag after a short delay to allow for any pending updates
    setTimeout(() => {
      isManagingGuidance.current = false;
    }, 0);
  }, [editor]);

  useEffect(() => {
    // Initial setup
    manageGuidanceNodes();

    // Handle when headings are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, () => {
      manageGuidanceNodes();
    });

    return () => {
      removeNodeTransform();
    };
  }, [editor, manageGuidanceNodes]);

  return null; // This plugin doesn't render anything
};
